import React from 'react';
import { ModuleAPI } from '@src/types/dotnet';

export function useDotNet<T>(url: string) {

    const dotnetUrl = React.useRef('');
    const [assembly, setAssembly] = React.useState<T | null>(null);
    const [loading, setLoading] = React.useState(true);

    const loadAsync = React.useCallback(
        async (currentUrl: string, abortSignal: AbortSignal): Promise<T> => {
            const module = await import(/* @vite-ignore */ currentUrl);
            abortSignal.throwIfAborted();

            if (!isModuleAPI(module)) {
                throw new Error(`Module is not ModuleAPI: `, module);
            }

            const { dotnet } = module;
            console.log(dotnet);

            const { getAssemblyExports, getConfig } = await dotnet
                .withDiagnosticTracing(false)
                .withConfig({
                    environmentVariables: {
                        "MONO_LOG_LEVEL": "debug",
                        "MONO_LOG_MASK": "all",
                    }
                })
                .create();

            const config = getConfig();
            if (!config.mainAssemblyName) {
                throw new Error(`'mainAssemblyName' is not defined`);
            }

            const exports = await getAssemblyExports(config.mainAssemblyName);
            return exports;
        },
        []
    );

    React.useEffect(
        () => {
            // safeguard to prevent double-loading
            if (dotnetUrl.current === url) {
                return;
            }

            const abortController = new AbortController();

            dotnetUrl.current = url;

            setLoading(true);
            loadAsync(url, abortController.signal)
                .then(x => {
                    setLoading(false);
                    setAssembly(x);
                })
                .catch(x => {
                    if (x instanceof Error && x.name === "AbortError") {
                        return;
                    }

                    window.alert(x);
                });

            return () => {
                abortController.abort();
            };
        },
        [loadAsync, url]
    );

    const result = React.useMemo(
        () => ({ assembly, loading }),
        [assembly, loading]
    );

    return result;
}

function isModuleAPI(x: unknown): x is ModuleAPI {
    return (
        x !== null &&
        typeof x === "object" &&
        "dotnet" in x &&
        "exit" in x
    );
}
